import {
  Component,
  computed,
  HostListener,
  input,
  output,
  signal,
} from '@angular/core';
import { InspectionModel, Lab } from '../../../model';
import { LabGaugeComponent } from './lab-gauge.component';

@Component({
  selector: 'app-lab-threshold-gauges',
  imports: [LabGaugeComponent],
  template: `
    <div class="gauge-panel"
      [style.left.px]="x()" [style.top.px]="y()"
      [style.width.px]="w()" [style.height.px]="h()">

      <div class="gauge-titlebar" (mousedown)="startDrag($event)">
        <span>LAB Gauges</span>
        <button class="gauge-close" (click)="closed.emit()">✕</button>
      </div>

      <div class="gauge-body">
        @for (ch of channels(); track ch.label) {
          <app-lab-gauge
            [label]="ch.label"
            [predicted]="ch.predicted"
            [ref]="ch.ref"
            [min]="ch.min"
            [max]="ch.max"/>
        }
      </div>

      <div class="gauge-resize" (mousedown)="startResize($event)">⇲</div>
    </div>
  `,
  styles: [`
    :host { display: contents; }

    .gauge-panel {
      position: fixed;
      z-index: 9000;
      background: white;
      border: 1px solid #bbb;
      border-radius: 6px;
      box-shadow: 0 4px 24px rgba(0,0,0,0.2);
      display: flex;
      flex-direction: column;
      min-width: 300px;
      min-height: 150px;
      overflow: hidden;
    }

    .gauge-titlebar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 5px 10px;
      background: #f0f0f0;
      border-bottom: 1px solid #ddd;
      cursor: move;
      user-select: none;
      font-weight: bold;
      font-size: 13px;
      flex-shrink: 0;
    }

    .gauge-close {
      border: none; background: transparent;
      cursor: pointer; font-size: 16px; color: #666; padding: 0 2px;
    }
    .gauge-close:hover { color: red; }

    .gauge-body {
      flex: 1; min-height: 0;
      display: flex; flex-direction: column;
      padding: 8px 10px; gap: 8px; overflow: hidden;
    }

    .gauge-resize {
      position: absolute; bottom: 2px; right: 4px;
      cursor: se-resize; font-size: 14px; color: #aaa;
      user-select: none;
    }
  `],
})
export class LabThresholdGaugesComponent {
  readonly predicted = input<Lab | undefined>(undefined);
  readonly reference = input<Lab | undefined>(undefined);
  readonly model     = input<InspectionModel | undefined>(undefined);
  readonly closed    = output<void>();

  readonly x = signal(120);
  readonly y = signal(120);
  readonly w = signal(Math.max(520, Math.floor(window.innerWidth / 2)));
  readonly h = signal(280);

  @HostListener('document:keydown.escape')
  onEscape() { this.closed.emit(); }

  readonly channels = computed(() => {
    const m   = this.model();
    const pred = this.predicted();
    const ref  = this.reference();
    if (!ref) return [];
    return [
      { label: 'L', predicted: pred?.l, ref: ref.l, min: m?.dlMinThreshold ?? -14, max: m?.dlMaxThreshold ?? 11 },
      { label: 'A', predicted: pred?.a, ref: ref.a, min: m?.daMinThreshold ?? -15, max: m?.daMaxThreshold ?? 12 },
      { label: 'B', predicted: pred?.b, ref: ref.b, min: m?.dbMinThreshold ?? -16, max: m?.dbMaxThreshold ?? 13 },
    ];
  });

  // ── Drag ──────────────────────────────────────────────────────────────────
  private dragging = false;
  private dragStartX = 0; private dragStartY = 0;
  private dragOriginX = 0; private dragOriginY = 0;

  startDrag(e: MouseEvent) {
    e.preventDefault();
    this.dragging = true;
    this.dragStartX = e.clientX; this.dragStartY = e.clientY;
    this.dragOriginX = this.x(); this.dragOriginY = this.y();
  }

  // ── Resize ────────────────────────────────────────────────────────────────
  private resizing = false;
  private resizeStartX = 0; private resizeStartY = 0;
  private resizeOriginW = 0; private resizeOriginH = 0;

  startResize(e: MouseEvent) {
    e.preventDefault(); e.stopPropagation();
    this.resizing = true;
    this.resizeStartX = e.clientX; this.resizeStartY = e.clientY;
    this.resizeOriginW = this.w(); this.resizeOriginH = this.h();
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(e: MouseEvent) {
    if (this.dragging) {
      this.x.set(this.dragOriginX + e.clientX - this.dragStartX);
      this.y.set(this.dragOriginY + e.clientY - this.dragStartY);
    }
    if (this.resizing) {
      this.w.set(Math.max(300, this.resizeOriginW + e.clientX - this.resizeStartX));
      this.h.set(Math.max(150, this.resizeOriginH + e.clientY - this.resizeStartY));
    }
  }

  @HostListener('document:mouseup')
  onMouseUp() { this.dragging = false; this.resizing = false; }
}
